import React from 'react';
import PropTypes from 'prop-types';
import { ModalBody, ModalContainer, ModalHeader } from 'components/modals';
import DialogModal from 'tradera-components/dialog/dialog-modal';
import { checkUserMessagingParticipation } from 'tradera-apps/my-tradera/app/helpers/messaging-helpers';
import ArrowRightIcon from '@tradera/blueprint/theme/icons/arrow-right.svg?jsx';
import CloseIcon from '@tradera/blueprint/theme/icons/close-narrow.svg?jsx';
import LoadIcon from '@tradera/blueprint/theme/icons/load.svg?jsx';
import { logger } from 'packages/logger';
import { withUrlLocalizer } from 'tradera-lang/translate';
import { getMessagingUrl } from 'tradera-components/messaging/get-messaging-url';

const MESSAGING_STATES = {
    LOADING: 'LOADING',
    READY: 'READY'
};
const POST_MESSAGE_TYPE_PREFIX = 'tradera.messaging.';

class MessagingModal extends React.Component {
    MESSAGING_REMOTE_URL;

    constructor(props) {
        super(props);
        this.state = {
            messaging: MESSAGING_STATES.LOADING,
            participating: props.participating || null
        };
        this.MESSAGING_REMOTE_URL = getMessagingUrl(props.toLocalizedUrl);
    }

    handleMessage = event => {
        const {
            data: { type },
            origin
        } = event;
        if (this.MESSAGING_REMOTE_URL.indexOf(origin) < 0) {
            return;
        }
        switch (type) {
            // Messages loaded & ready
            case POST_MESSAGE_TYPE_PREFIX + 'ready':
                this.setState({ messaging: MESSAGING_STATES.READY });
                break;
            // Unread threads update
            case POST_MESSAGE_TYPE_PREFIX + 'close':
                this.handleClose();
                break;
        }
    };

    handleClose = () => {
        this.props.closeModal();
    };

    setIframeRef = element => {
        if (element) {
            this.iframeElement = element;
        }
        // Focus on iframe content window to allow focus to move into iframe
        setTimeout(() => {
            if (this.iframeElement && this.iframeElement.contentWindow) {
                this.iframeElement.contentWindow.focus();
            }
        }, 0);
    };

    componentDidMount() {
        if (this.state.participating === null) {
            checkUserMessagingParticipation(this.props.recipientId)
                .then(participating => this.setState({ participating }))
                .catch(logger);
        }
        window.addEventListener('message', this.handleMessage);
        this.setIframeRef();
    }

    componentWillUnmount() {
        window.removeEventListener('message', this.handleMessage);
    }

    render() {
        const { title, src, item, toLocalizedUrl } = this.props;
        const { participating } = this.state;
        return (
            <DialogModal onDismiss={() => this.handleClose()}>
                <ModalContainer onClose={() => this.handleClose()}>
                    {title && (
                        <ModalHeader onClose={() => this.handleClose()}>
                            {title}
                        </ModalHeader>
                    )}
                    <ModalBody
                        className={`messaging-modal__body ${
                            participating
                                ? 'messaging-modal__body--with-iframe'
                                : ''
                        }`}>
                        {participating === true && (
                            <iframe
                                className="messaging-iframe"
                                scrolling="no"
                                src={this.MESSAGING_REMOTE_URL + src}
                                ref={this.setIframeRef}
                            />
                        )}
                        {participating === false && (
                            <NotParticipatingMessage
                                item={item}
                                onClose={() => this.handleClose()}
                                toLocalizedUrl={toLocalizedUrl}
                            />
                        )}
                        {participating === null && <Loader />}
                    </ModalBody>
                </ModalContainer>
            </DialogModal>
        );
    }
}

MessagingModal.propTypes = {
    title: PropTypes.string,
    src: PropTypes.string.isRequired
};

const Loader = () => {
    return (
        <div className="d-flex h-100 w-100 justify-content-center align-items-center">
            <LoadIcon className="icon icon-secondary icon-lg icon-spin" />
        </div>
    );
};

const NotParticipatingMessage = props => {
    const { item, onClose, toLocalizedUrl } = props;
    if (item) {
        return (
            <div className="d-flex h-100 w-100 justify-content-center align-items-center">
                <div className="p-4">
                    <h2 className="text-center">{item.seller.alias}</h2>
                    <p className="text-center mb-5">
                        <span className="badge badge-dark">Butik</span>
                    </p>
                    <div style={{ maxWidth: '35rem' }}>
                        <p className="alert alert-secondary mb-5">
                            {item.seller.alias} är inte en privat säljare och
                            finns för tillfället inte tillgänglig via Traderas
                            meddelandetjänst.
                        </p>
                        <p className="mb-5">
                            Vi rekommenderar att du kontaktar dem via
                            orderinformation för detta köp.
                        </p>
                    </div>
                    <div
                        style={{ maxWidth: '30rem' }}
                        className="mx-auto align-items-center">
                        <a
                            className="btn btn-primary btn-fluid btn--icon-fixed mb-3"
                            href={toLocalizedUrl(
                                `/my/order/${item.transaction.purchaseOrderId}`
                            )}>
                            <ArrowRightIcon className="icon icon-after" />
                            <span>Orderinformation</span>
                        </a>
                        <button
                            className="btn btn-outline-dark btn-fluid btn--icon-fixed"
                            onClick={onClose}>
                            <CloseIcon className="icon icon-before" />
                            <span>Stäng</span>
                        </button>
                    </div>
                </div>
            </div>
        );
    } else {
        return (
            <div className="d-flex h-100 w-100 justify-content-center align-items-center">
                <div style={{ maxWidth: '35rem' }}>
                    <p className="alert alert-secondary mb-5">
                        Säljaren finns för tillfället inte tillgänglig via
                        Traderas meddelandetjänst.
                    </p>
                </div>
                <div
                    style={{ maxWidth: '30rem' }}
                    className="mx-auto align-items-center">
                    <button
                        className="btn btn-outline-dark btn-fluid btn--icon-fixed"
                        onClick={onClose}>
                        <CloseIcon className="icon icon-before" />
                        <span>Stäng</span>
                    </button>
                </div>
            </div>
        );
    }
};

export default withUrlLocalizer(MessagingModal);
